(() => {
  const P = () => window.GhaliaPricing;
  const moneyNet = (cents) => P()?.formatNet(cents) ?? (Number(cents) / 100).toFixed(2);
  const moneyDisplay = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);

  const formatPromoAmount = (netCents, drawer) =>
    drawer && promoCfg()?.formatPromoSummaryAmount
      ? promoCfg().formatPromoSummaryAmount(netCents, drawer)
      : promoCfg()?.formatAmount?.(netCents)
        ? `-${promoCfg().formatAmount(netCents)}`
        : `-${moneyNet(netCents)}`;

  const summaryFreeLabel = (drawer, el) =>
    el?.dataset?.freeLabel ||
    window.Shopify?.strings?.cart?.shippingFree ||
    'Free';

  const renderSummaryFreeValue = (compareGrossCents, freeLabel) => {
    const pricing = P();
    if (!pricing || compareGrossCents <= 0) {
      return `<span class="cart-summary__free">${freeLabel}</span>`;
    }
    const compare = pricing.formatDisplay(compareGrossCents);
    return `<span class="cart-summary__free-value"><span class="cart-summary__free">${freeLabel}</span><del class="cart-summary__compare">${compare}</del></span>`;
  };

  const promoGrossSavingsCents = (netSavings, drawer) => {
    const pricing = P();
    const cfg = promoCfg()?.read?.(drawer);
    const maxNet = promoCfg()?.maxSavingsNet?.(drawer) || 0;
    if (!pricing || netSavings <= 0) return 0;
    if (maxNet > 0 && netSavings >= maxNet - 1 && cfg?.maxDiscountSar > 0) {
      return promoCfg().grossCentsFromSar(cfg.maxDiscountSar);
    }
    return pricing.displayCents(netSavings);
  };

  const renderSummarySavingsAmount = (grossCents) => {
    const pricing = P();
    if (!pricing || grossCents <= 0) return '';
    return `<span class="cart-summary__savings-amount">−${pricing.formatDisplay(grossCents)}</span>`;
  };

  const giftCompareGrossCents = (drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return 0;
    const retailNet = configuredGiftRetailNet(drawer);
    if (retailNet > 0) return pricing.displayCents(retailNet);
    return Number(promoCfg()?.grossCentsFromSar?.(280) || 28000);
  };

  const shippingFlatGrossCents = (drawer) => {
    const summary = drawer?.querySelector('[data-cart-summary]');
    return Number(summary?.dataset.shippingFlatCents) || 2500;
  };

  const readThemeConfig = (drawer) => ({
    enabled: drawer?.dataset.globalDiscountEnabled === 'true',
    pct: Number(drawer?.dataset.globalDiscountPct || 0),
    maxNet: Number(drawer?.dataset.globalDiscountMaxNet || 0),
    label: drawer?.dataset.globalDiscountLabel || 'Extra savings',
  });

  const vatRateFromDrawer = (drawer) =>
    Number(drawer?.querySelector('[data-cart-summary]')?.dataset.vatRate || 15);

  /** Theme SAR settings are VAT-inclusive customer amounts → ex-VAT cents for cart math. */
  const sarGrossToNetCents = (sar, drawer) => {
    if (!sar) return 0;
    const vatRate = vatRateFromDrawer(drawer);
    return Math.round((sar * 100 * 100) / (100 + vatRate));
  };

  const promoCfg = () => window.GhaliaPromoConfig;
  const qualifyingMeetsPromoMinimum = (qualifyingNet, drawer) =>
    promoCfg()?.meetsMinimum?.(qualifyingNet, drawer) ?? true;
  const capPromoSavings = (savings, drawer) =>
    promoCfg()?.capNetSavings?.(savings, drawer) ?? savings;

  const PROMO_STORAGE_KEY = 'ghalia_cart_promo_code';

  const storedPromoCode = () => {
    try {
      const raw = sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
      return window.GhaliaPromoConfig?.sanitizeStoredCode?.(raw) ?? raw;
    } catch (_) {
      return '';
    }
  };

  const isPromoCodeApplication = (app) =>
    window.GhaliaPromoConfig?.isDiscountCodeApplication?.(app) ?? app?.type === 'discount_code';

  const isGiftDiscountApplication = (app) =>
    window.GhaliaPromoConfig?.isGiftDiscountApplication?.(app) ??
    /^GHALIA CART GIFT/i.test(String(app?.title || ''));

  const giftDiscountAllocationNet = (cart) =>
    window.GhaliaPromoConfig?.giftDiscountAllocationNet?.(cart) ??
    (cart?.items || []).reduce(
      (sum, item) =>
        sum +
        (item.line_level_discount_allocations || []).reduce((lineSum, alloc) => {
          if (!isGiftDiscountApplication(alloc?.discount_application)) return lineSum;
          return lineSum + (Number(alloc.amount) || 0);
        }, 0),
      0
    );

  /** Theme free-gift lines — catalog retail from drawer config (280 SAR inc VAT = 243.48 net). */
  const configuredGiftRetailNet = (drawer) =>
    Number(drawer?.dataset?.cartGiftRetailNetCents || 0);

  const themeFreeGiftSavingsNet = (cart, drawer, pricing) => {
    if (!pricing || !drawer) return 0;
    const threshold = Number(drawer.dataset?.cartGiftThreshold || 0);
    const qualifying = pricing.qualifyingSubtotalNet(cart, drawer);
    const qualifies = threshold <= 0 ? qualifying > 0 : qualifying >= threshold;
    if (!qualifies) return 0;

    const retailNet = configuredGiftRetailNet(drawer);
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (!pricing.isFreeGiftLine(item)) return;
      const qty = Number(item.quantity) || 1;
      if (retailNet > 0) {
        sum += retailNet * qty;
        return;
      }
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      const catalog =
        Number(row?.dataset?.cartLineNet || 0) ||
        Number(item.original_line_price) ||
        Number(item.original_price ?? item.price ?? 0) * qty;
      if (catalog > 0) sum += catalog;
    });
    return sum;
  };

  const cartQualifiesForFreeGift = (cart, drawer, pricing) => {
    if (!drawer || !pricing) return false;
    const threshold = Number(drawer.dataset?.cartGiftThreshold || 0);
    const qualifying = pricing.qualifyingSubtotalNet(cart, drawer);
    if (threshold <= 0) {
      return (cart?.items || []).some((item) => pricing.isPromoPaidLine?.(item));
    }
    return qualifying >= threshold;
  };

  const promoTitleFromLines = (cart) => {
    for (const item of cart?.items || []) {
      for (const alloc of item.line_level_discount_allocations || []) {
        if (!isPromoCodeApplication(alloc?.discount_application)) continue;
        const title = alloc.discount_application?.title;
        if (title) return String(title).trim();
      }
    }
    return '';
  };

  const inferredPromoSavingsNet = (cart, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return 0;
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isNativeBundleLine(item) || pricing.isFreeGiftLine(item)) return;
      if (giftVariantId && String(item.variant_id) === giftVariantId) return;
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      const domNet = Number(row?.dataset?.cartLineNet || 0);
      const originalNet =
        domNet > 0 ? domNet : Number(item.original_line_price) || Number(item.line_price) || 0;
      const finalNet = Number(item.final_line_price) || 0;
      if (originalNet > finalNet) sum += originalNet - finalNet;
    });
    return sum;
  };

  const cartDiscountCodeTitle = (cart) =>
    window.GhaliaPromoConfig?.cartDiscountCodeTitle?.(cart) || '';

  const promoCodeApplication = (cart, drawer) => {
    const apps = cart?.cart_level_discount_applications || [];
    const fromCart = apps.find((d) => isPromoCodeApplication(d));
    if (fromCart?.title) return fromCart;

    const lineTitle = promoTitleFromLines(cart);
    const fromLines = linePromoAllocationNet(cart, drawer);
    const inferred = drawer ? inferredPromoSavingsNet(cart, drawer) : 0;
    const savings = fromLines > 0 ? fromLines : inferred;
    const stored = storedPromoCode();
    const title = lineTitle || stored;

    if (!title) return null;

    const allocated = savings > 0 ? savings : 0;
    if (allocated <= 0) return null;
    return {
      type: 'discount_code',
      title,
      total_allocated_amount: allocated,
      value_type: 'percentage',
      value: 10,
    };
  };

  const promoPercent = (promo) => {
    const raw = Number(promo?.value) || 0;
    if (promo?.value_type !== 'percentage') return 0;
    return raw <= 1 ? raw * 100 : raw;
  };

  const linePromoAllocationNet = (cart, drawer) => {
    const pricing = P();
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    return (cart?.items || []).reduce((sum, item) => {
      if (pricing?.isPromoPaidLine && !pricing.isPromoPaidLine(item)) return sum;
      if (pricing?.isNativeBundleLine?.(item) || pricing?.isFreeGiftLine?.(item)) return sum;
      if (giftVariantId && String(item.variant_id) === giftVariantId) return sum;
      return (
        sum +
        (item.line_level_discount_allocations || []).reduce((lineSum, alloc) => {
          if (!isPromoCodeApplication(alloc?.discount_application)) return lineSum;
          return lineSum + (Number(alloc.amount) || 0);
        }, 0)
      );
    }, 0);
  };

  const syncLineCustomerAmounts = (row, pricing, netCents) => {
    if (!row || !pricing?.displayCents) return;
    const net = Number(netCents) || 0;
    if (net <= 0) return;
    row.dataset.cartLineCustomerNet = String(net);
    row.dataset.cartLineDisplay = String(pricing.displayCents(net));
  };

  const syncLineCompareDisplay = (row, pricing, netCents) => {
    if (!row || !pricing?.displayCents) return;
    const net = Number(netCents) || 0;
    if (net <= 0) return;
    row.dataset.cartLineCompareDisplay = String(pricing.displayCents(net));
  };

  /** Catalog ex-VAT subtotal (pre promo) — mirrors cart-line-net-cents / data-cart-line-net. */
  const catalogNetSubtotal = (cart, drawer, pricing) => {
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      if (giftVariantId && String(item.variant_id) === giftVariantId) return;
      if (pricing.isGiftCatalogVariant?.(item)) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const catalog =
        Number(row?.dataset?.cartLineNet || 0) ||
        Number(item.original_line_price) ||
        Number(item.original_price ?? item.price ?? 0) * (Number(item.quantity) || 1);
      sum += catalog;
    });
    return sum;
  };

  /** Shopify code promos update line final_line_price — do not subtract promo row twice. */
  const promoAlreadyInSubtotal = (cart, drawer, pricing, promoSavings, netSubtotal) => {
    if (promoSavings <= 0) return false;
    if (linePromoAllocationNet(cart, drawer) > 0) return true;
    const catalogNet = catalogNetSubtotal(cart, drawer, pricing);
    if (catalogNet > netSubtotal && catalogNet - netSubtotal >= promoSavings - 1) return true;
    const inferred = inferredPromoSavingsNet(cart, drawer);
    return inferred > 0 && Math.abs(inferred - promoSavings) <= 1;
  };

  const promoLabelForCart = (cart, drawer) => {
    const lineTitle = promoTitleFromLines(cart);
    const stored = storedPromoCode();
    const shopifyTitle = cartDiscountCodeTitle(cart);
    return lineTitle || shopifyTitle || stored || '';
  };

  const cartHasPromoCode = (cart) =>
    Boolean(cartDiscountCodeTitle(cart) || storedPromoCode() || promoTitleFromLines(cart));

  const promoCodeIntent = (cart, drawer) => {
    if (cartDiscountCodeTitle(cart)) return true;
    if (promoTitleFromLines(cart)) return true;
    if (storedPromoCode()) return true;
    if (linePromoAllocationNet(cart, drawer) > 0) return true;
    const pricing = P();
    if (!pricing || !drawer) return false;
    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    if (qualifyingNet <= 0 || !qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) return false;
    return Boolean(promoCfg()?.read?.(drawer)?.defaultCode);
  };

  /** Shopify code promo + theme WELCOME preview (split across paid lines, never on gift). */
  const resolvePromoSavingsNet = (cart, drawer) => {
    const fromLines = linePromoAllocationNet(cart, drawer);
    if (fromLines > 0) return capPromoSavings(fromLines, drawer);

    const apps = cart?.cart_level_discount_applications || [];
    const codeApp = apps.find((d) => isPromoCodeApplication(d));
    const fromCart = Number(codeApp?.total_allocated_amount) || 0;
    if (fromCart > 0) return capPromoSavings(fromCart, drawer);

    const pricing = P();
    if (!pricing || !drawer || !promoCodeIntent(cart, drawer)) return 0;
    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    if (qualifyingNet <= 0 || !qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) return 0;
    if (linePromoAllocationNet(cart, drawer) > 0) return 0;
    return promoCfg()?.computeSavingsNet?.(qualifyingNet, drawer) || 0;
  };

  const resolvePromoState = (cart, drawer, stackState, cfg) => {
    const pricing = P();
    if (!pricing || !drawer) return { savings: 0, label: '', active: false };

    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    if (qualifyingNet <= 0 || !qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) {
      return { savings: 0, label: '', active: false };
    }

    if (!promoCodeIntent(cart, drawer)) {
      return { savings: 0, label: '', active: false };
    }

    const label = promoLabelForCart(cart, drawer);
    const savingsNet = resolvePromoSavingsNet(cart, drawer);
    if (savingsNet > 0) {
      return { savings: capPromoSavings(savingsNet, drawer), label, active: true };
    }
    return { savings: 0, label, active: false };
  };

  const lineUnitNet = (item, row, pricing) => {
    const qty = Number(item.quantity) || 1;
    const { net, unitNet } = pricing.cartLineAmounts(item, row);
    if (unitNet > 0) return unitNet;
    return Math.round(net / qty);
  };

  const lineOriginalNet = (item, row) => {
    const qty = Number(item.quantity) || 1;
    const origLine = Number(item.original_line_price) || Number(item.line_price) || 0;
    if (origLine > 0) return origLine;
    const domNet = Number(row?.dataset?.cartLineNet || 0);
    const domQty = Number(row?.querySelector('[data-cart-line-qty]')?.value) || qty;
    if (domNet > 0 && domQty > 0) return Math.round(domNet * (qty / domQty));
    if (domNet > 0) return domNet;
    const unit = Number(item.original_price ?? item.price ?? 0);
    if (unit > 0) return unit * qty;
    return Number(item.line_price) || 0;
  };

  /** Sum VAT-inclusive catalog totals per paid line (excludes free gift). */
  const sumPaidLinesCatalogGross = (cart, drawer, pricing) => {
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      if (giftVariantId && String(item.variant_id) === giftVariantId) return;
      if (pricing.isGiftCatalogVariant?.(item)) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const compareGross = Number(row?.dataset?.cartLineCompareDisplay || 0);
      const catalogNet = lineOriginalNet(item, row);
      const catalogGross = catalogNet > 0 ? pricing.displayCents(catalogNet) : 0;
      sum += compareGross > catalogGross ? compareGross : catalogGross;
    });
    return sum;
  };

  /** Sum VAT-inclusive line totals as shown on each paid row (excludes free gift). */
  const sumPaidLinesDisplayGross = (cart, drawer, pricing) => {
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      if (giftVariantId && String(item.variant_id) === giftVariantId) return;
      if (pricing.isGiftCatalogVariant?.(item)) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const display = Number(row?.dataset?.cartLineDisplay || 0);
      if (display > 0) {
        sum += display;
        return;
      }
      const net =
        Number(row?.dataset?.cartLineCustomerNet || 0) ||
        Number(item.final_line_price) ||
        lineOriginalNet(item, row);
      if (net > 0) sum += pricing.displayCents(net);
    });
    return sum;
  };

  const sumPaidLinesCustomerNet = (cart, drawer, pricing) => {
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      if (giftVariantId && String(item.variant_id) === giftVariantId) return;
      if (pricing.isGiftCatalogVariant?.(item)) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const net =
        Number(row?.dataset?.cartLineCustomerNet || 0) ||
        Number(item.final_line_price) ||
        lineOriginalNet(item, row);
      if (net > 0) sum += net;
    });
    return sum;
  };

  const setCustomerLinePrice = (currentEl, pricing, netCents, row) => {
    if (!currentEl || !pricing) return;
    syncLineCustomerAmounts(row, pricing, netCents);
    const next = pricing.formatCustomerLineNet
      ? pricing.formatCustomerLineNet(netCents, row)
      : pricing.formatNet(netCents);
    if (currentEl.textContent.trim() !== next.trim()) currentEl.textContent = next;
  };

  const showPromoLinePrice = (priceEl, compareEl, currentEl, pricing, originalNet, finalNet, row) => {
    if (!currentEl || originalNet <= 0 || finalNet <= 0) return;
    if (finalNet >= originalNet) {
      if (compareEl) {
        compareEl.hidden = true;
        compareEl.setAttribute('hidden', '');
      }
      priceEl?.classList.remove('is-promo-discounted');
      syncLineCustomerAmounts(row, pricing, originalNet);
      return;
    }
    syncLineCustomerAmounts(row, pricing, finalNet);
    currentEl.textContent = pricing.formatCustomerLineNet(finalNet, row, { forceComputed: true });
    if (compareEl) {
      compareEl.textContent = pricing.formatNet(originalNet);
      compareEl.hidden = false;
      compareEl.removeAttribute('hidden');
      syncLineCompareDisplay(row, pricing, originalNet);
    }
    priceEl?.classList.add('is-promo-discounted');
    priceEl?.classList.remove('is-stack-discounted');
  };

  const bundleAndPromoUnits = (item, stackState) => {
    const pricing = P();
    const qty = Number(item.quantity) || 1;
    if (pricing?.isNativeBundleLine?.(item)) {
      return { bundleUnits: qty, promoUnits: 0, qty };
    }
    const fromLine = stackState?.bundleUnitsByLine?.get(item.key);
    const bundleUnits =
      stackState?.memberSet?.has(item.handle) && fromLine > 0
        ? fromLine
        : 0;
    const promoUnits = Math.max(0, qty - bundleUnits);
    return { bundleUnits, promoUnits, qty };
  };

  const applyFreeGiftLinePrices = (cart, drawer, pricing) => {
    if (!pricing || !drawer) return;
    const giftVariantId = drawer.dataset?.cartGiftVariantId || '';
    (cart?.items || []).forEach((item) => {
      if (!pricing.isFreeGiftLine(item)) {
        if (!giftVariantId || String(item.variant_id) !== giftVariantId) return;
        if (Number(item.final_line_price) > 0) return;
      }
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      if (!row) return;
      const priceEl = row.querySelector('[data-cart-line-price]') || row.querySelector('.cart-line__price');
      const compareEl = row.querySelector('[data-cart-line-compare]');
      const currentEl = row.querySelector('[data-cart-line-current]');
      if (!priceEl) return;

      const catalogNet =
        Number(row.dataset?.cartLineNet || 0) ||
        Number(item.original_line_price) ||
        Number(item.original_price ?? item.price ?? 0) * (Number(item.quantity) || 1);
      const retailNet = configuredGiftRetailNet(drawer);
      const compareNet =
        catalogNet > 0 ? catalogNet : retailNet > 0 ? retailNet * (Number(item.quantity) || 1) : 0;
      const compareDisplay = Number(row.dataset?.cartLineCompareDisplay || 0);

      if (compareEl) {
        const compareText =
          compareNet > 0
            ? pricing.formatNet(compareNet)
            : compareDisplay > 0
              ? pricing.formatDisplay(compareDisplay)
              : '';
        if (compareText) {
          compareEl.textContent = compareText;
          compareEl.hidden = false;
          compareEl.removeAttribute('hidden');
        }
      }
      if (currentEl) {
        currentEl.textContent =
          window.Shopify?.strings?.cart?.freeGiftLineFree ||
          priceEl.querySelector('[data-cart-line-current]')?.textContent?.trim() ||
          'FREE';
      }
      priceEl.classList.add('is-promo-discounted');
    });
  };

  const applyPromoLinePrices = (cart, drawer, stackState, promoSavings, qualifyingNet) => {
    const pricing = P();
    if (!pricing || !drawer) return;
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    const skipGiftLine = (item) =>
      pricing.isFreeGiftLine(item) ||
      (giftVariantId && String(item.variant_id) === giftVariantId);
    if (!qualifyingMeetsPromoMinimum(qualifyingNet, drawer)) {
      (cart?.items || []).forEach((item) => {
        if (skipGiftLine(item) || pricing.isNativeBundleLine(item)) return;
        const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
        if (!row) return;
        const priceEl = row.querySelector('[data-cart-line-price]');
        const compareEl = row.querySelector('[data-cart-line-compare]');
        const currentEl = row.querySelector('[data-cart-line-current]');
        if (!currentEl) return;
        const originalNet = lineOriginalNet(item, row);
        if (originalNet > 0) setCustomerLinePrice(currentEl, pricing, originalNet, row);
        if (compareEl) compareEl.hidden = true;
        priceEl?.classList.remove('is-promo-discounted');
      });
      return;
    }
    if (promoSavings <= 0) return;

    (cart?.items || []).forEach((item) => {
      if (skipGiftLine(item) || pricing.isNativeBundleLine(item)) return;
      if (!pricing.isPromoPaidLine?.(item)) return;

      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      if (!row) return;
      const priceEl = row.querySelector('[data-cart-line-price]');
      const compareEl = row.querySelector('[data-cart-line-compare]');
      const currentEl = row.querySelector('[data-cart-line-current]');
      if (!priceEl || !currentEl) return;

      const originalNet = lineOriginalNet(item, row);

      if (promoCfg()?.shopifyCodePromoWithinCap?.(cart, drawer)) {
        const promoAllocNet = promoCfg()?.lineCodePromoAllocNet?.(item) || 0;
        const finalNet =
          Number(item.final_line_price) ||
          (promoAllocNet > 0 ? Math.max(0, originalNet - promoAllocNet) : 0);
        if (finalNet > 0 && finalNet < originalNet) {
          showPromoLinePrice(priceEl, compareEl, currentEl, pricing, originalNet, finalNet, row);
          return;
        }
      }

      const { bundleUnits, promoUnits: bundlePromoUnits } = bundleAndPromoUnits(item, stackState);
      let promoUnits = bundlePromoUnits;
      if (promoUnits <= 0 && bundleUnits <= 0 && !pricing.isNativeBundleLine(item)) {
        promoUnits = Number(item.quantity) || 1;
      }
      if (promoUnits <= 0 && bundleUnits <= 0) return;

      const qtySafe = Number(item.quantity) || 1;
      const unitOriginalNet = Math.round(originalNet / qtySafe);

      let promoLineNet = unitOriginalNet * promoUnits;
      let finalPromoNet = promoLineNet;
      if (promoUnits > 0 && promoSavings > 0 && qualifyingNet > 0) {
        const lineShare = Math.round((promoSavings * promoLineNet) / qualifyingNet);
        finalPromoNet = Math.max(0, promoLineNet - lineShare);
      } else if (promoUnits > 0 && promoSavings > 0) {
        const catalogNet = catalogNetSubtotal(cart, drawer, pricing);
        const splitBase = catalogNet > 0 ? catalogNet : originalNet;
        if (splitBase > 0) {
          const lineShare = Math.round((promoSavings * originalNet) / splitBase);
          finalPromoNet = Math.max(0, promoLineNet - lineShare);
        }
      }

      let bundlePartNet = 0;
      if (bundleUnits > 0 && stackState?.qualified && stackState?.pct > 0) {
        const unitNet = lineUnitNet(item, row, pricing);
        const discountedUnit = pricing.applyDiscount(unitNet, stackState.pct);
        bundlePartNet = discountedUnit * bundleUnits;
      } else if (bundleUnits > 0) {
        bundlePartNet = unitOriginalNet * bundleUnits;
      }

      const currentNet = bundlePartNet + finalPromoNet;
      const compareNet = originalNet;

      if (currentNet < compareNet) {
        showPromoLinePrice(priceEl, compareEl, currentEl, pricing, compareNet, currentNet, row);
      }
    });
  };

  const hideLiquidDiscountRows = (drawer) => {
    drawer?.querySelectorAll('[data-cart-liquid-discount]').forEach((el) => {
      el.hidden = true;
    });
  };

  /** Theme shipping SAR settings are already VAT-inclusive customer amounts. Always charged. */
  const estimateShippingDisplay = (summary) => {
    if (!summary) return 0;
    return Number(summary.dataset.shippingFlatCents) || 2500;
  };

  const shippingDisplayCents = (drawer) => {
    const summary = drawer?.querySelector('[data-cart-summary]');
    if (!summary) return 0;
    const fromEstimate = Number(summary.dataset.shippingNetCents);
    if (Number.isFinite(fromEstimate) && fromEstimate > 0) return fromEstimate;
    return estimateShippingDisplay(summary);
  };

  /** Sum of Shopify final_line_price (ex-VAT) — matches checkout subtotal after line discounts. */
  const finalItemsNet = (cart, pricing, drawer) => {
    const giftVariantId = drawer?.dataset?.cartGiftVariantId || '';
    let sum = 0;
    (cart?.items || []).forEach((item) => {
      if (pricing.isFreeGiftLine(item)) return;
      if (giftVariantId && String(item.variant_id) === giftVariantId) return;
      if (pricing.isGiftCatalogVariant?.(item)) return;
      sum += Number(item.final_line_price) || Number(item.line_price) || 0;
    });
    return sum;
  };

  const applySummary = (cart, stackState, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return;

    hideLiquidDiscountRows(drawer);

    const cfg = readThemeConfig(drawer);
    const globalRow = drawer.querySelector('[data-cart-global-discount-row]');
    const summary = drawer.querySelector('[data-cart-summary]');
    const promoRow = drawer.querySelector('[data-cart-promo-code-row]');
    const promoLabel = drawer.querySelector('[data-cart-promo-code-label]');
    const promoAmount = drawer.querySelector('[data-cart-promo-code-amount]');
    const giftRow = drawer.querySelector('[data-cart-gift-discount-row]');
    const giftLabel = drawer.querySelector('[data-cart-gift-discount-label]');
    const giftAmount = drawer.querySelector('[data-cart-gift-discount-amount]');
    const bundleRow = drawer.querySelector('[data-cart-bundle-benefit-row]');
    const bundleLabel = drawer.querySelector('[data-cart-bundle-benefit-label]');
    const bundleAmount = drawer.querySelector('[data-cart-bundle-benefit-amount]');
    const benefitsEl = drawer.querySelector('[data-cart-summary-benefits]');
    const shippingBenefitRow = drawer.querySelector('[data-cart-shipping-benefit-row]');
    const shippingBenefitEl = drawer.querySelector('[data-cart-summary-shipping-benefit]');
    const totalSavingsRow = drawer.querySelector('[data-cart-total-savings-row]');
    const totalSavingsAmount = drawer.querySelector('[data-cart-total-savings-amount]');
    const subtotalEl = drawer.querySelector('[data-cart-summary-subtotal]');
    const shippingEl = drawer.querySelector('[data-cart-summary-shipping]');
    const shippingRow = shippingEl?.closest('.cart-summary__row--shipping');
    const vatEl = drawer.querySelector('[data-cart-summary-vat]');
    const vatRow = vatEl?.closest('.cart-summary__row--vat');
    const totalEl = drawer.querySelector('[data-cart-summary-total]');
    const pricesIncludeVat = pricing.catalogPricesExcludeVat?.() ?? true;

    if (vatRow) {
      vatRow.hidden = true;
      vatRow.setAttribute('hidden', '');
    }

    const netSubtotal = stackState?.netSubtotalCents || 0;
    const catalogNet = catalogNetSubtotal(cart, drawer, pricing);
    const bundleSavings = stackState?.bundleSavingsNetCents || 0;
    const qualifyingNet = pricing.qualifyingSubtotalNet(cart, drawer);
    const promo = resolvePromoState(cart, drawer, stackState, cfg);
    const promoDisplaySavings = promo.active ? promo.savings : 0;
    const promoLabelText = promo.label || '';

    if (globalRow) globalRow.hidden = true;

    const stackRowOutside = drawer.querySelector('[data-cart-stack-discount-row]');
    if (stackRowOutside) {
      stackRowOutside.hidden = true;
      stackRowOutside.setAttribute('hidden', '');
    }

    if (promoRow && promoLabel && promoAmount) {
      if (promoDisplaySavings > 0 && promoLabelText) {
        promoRow.hidden = false;
        promoRow.removeAttribute('hidden');
        const codeLabel = String(promoLabelText).toUpperCase();
        promoLabel.textContent =
          promoLabel.dataset.codeTemplate?.replace('__TITLE__', codeLabel) || codeLabel;
        promoAmount.innerHTML = `<span class="cart-summary__savings-amount">${formatPromoAmount(promoDisplaySavings, drawer)}</span>`;
      } else {
        promoRow.hidden = true;
        promoRow.setAttribute('hidden', '');
        promoLabel.textContent = '';
        promoAmount.textContent = '';
        promoAmount.innerHTML = '';
      }
    }

    const shopifyGiftSavings = giftDiscountAllocationNet(cart);
    const themeGiftSavings = themeFreeGiftSavingsNet(cart, drawer, pricing);
    const qualifiesForGift = cartQualifiesForFreeGift(cart, drawer, pricing);
    const hasConfiguredGift = (cart?.items || []).some((item) => pricing.isFreeGiftLine(item));
    let giftDisplaySavings = 0;
    if (qualifiesForGift && hasConfiguredGift && themeGiftSavings > 0) {
      giftDisplaySavings = themeGiftSavings;
    } else if (qualifiesForGift && hasConfiguredGift && shopifyGiftSavings > 0) {
      giftDisplaySavings = shopifyGiftSavings;
    }

    if (giftRow && giftLabel && giftAmount) {
      if (giftDisplaySavings > 0 && qualifiesForGift && hasConfiguredGift) {
        giftRow.hidden = false;
        giftRow.removeAttribute('hidden');
        const giftFreeLabel = summaryFreeLabel(drawer, giftAmount);
        giftAmount.innerHTML = renderSummaryFreeValue(giftCompareGrossCents(drawer), giftFreeLabel);
      } else {
        giftRow.hidden = true;
        giftRow.setAttribute('hidden', '');
        giftAmount.textContent = '';
        giftAmount.innerHTML = '';
      }
    }

    applyPromoLinePrices(cart, drawer, stackState, promoDisplaySavings, qualifyingNet);
    applyFreeGiftLinePrices(cart, drawer, pricing);

    const itemsCatalogNet = catalogNet > 0 ? catalogNet : netSubtotal;
    const paidLinesCatalogGross = sumPaidLinesCatalogGross(cart, drawer, pricing);
    const shopifyTotalNet = Number(cart?.total_price) || 0;
    if (itemsCatalogNet <= 0 && paidLinesCatalogGross <= 0 && shopifyTotalNet <= 0) return;

    const shopifyItemsNet =
      shopifyTotalNet > 0 ? shopifyTotalNet : finalItemsNet(cart, pricing, drawer);
    const netBeforePromo = Math.max(0, itemsCatalogNet - bundleSavings);
    let netAfterDiscounts;
    if (shopifyItemsNet > 0) {
      netAfterDiscounts = shopifyItemsNet;
    } else if (promoDisplaySavings > 0) {
      netAfterDiscounts = Math.max(0, netBeforePromo - promoDisplaySavings);
    } else {
      netAfterDiscounts = netBeforePromo;
    }
    const shippingDisplay = shippingDisplayCents(drawer);
    const shippingIsFree = shippingDisplay === 0;
    const showGiftBenefit =
      giftDisplaySavings > 0 && qualifiesForGift && hasConfiguredGift;
    const showPromoBenefit = promoDisplaySavings > 0 && promoLabelText;
    const showBundleBenefit = bundleSavings > 0;

    if (bundleRow && bundleLabel && bundleAmount) {
      if (showBundleBenefit) {
        bundleRow.hidden = false;
        bundleRow.removeAttribute('hidden');
        const pct = stackState?.pct || 0;
        const isCompleteLook = (stackState?.memberSet?.size || 0) > 0;
        const template = bundleLabel.dataset?.template || '';
        const fallback = bundleLabel.dataset?.fallback || 'Bundle savings';
        if (isCompleteLook && pct > 0 && template.includes('__PCT__')) {
          bundleLabel.textContent = template.replace('__PCT__', String(pct));
        } else if (pct > 0 && template.includes('__PCT__')) {
          bundleLabel.textContent = template.replace('__PCT__', String(pct));
        } else {
          bundleLabel.textContent = fallback;
        }
        bundleAmount.innerHTML = renderSummarySavingsAmount(pricing.displayCents(bundleSavings));
      } else {
        bundleRow.hidden = true;
        bundleRow.setAttribute('hidden', '');
        bundleLabel.textContent = '';
        bundleAmount.innerHTML = '';
      }
    }

    if (shippingBenefitRow && shippingBenefitEl) {
      if (shippingIsFree) {
        shippingBenefitRow.hidden = false;
        shippingBenefitRow.removeAttribute('hidden');
        const freeLabel = summaryFreeLabel(drawer, shippingBenefitEl);
        shippingBenefitEl.innerHTML = renderSummaryFreeValue(
          shippingFlatGrossCents(drawer),
          freeLabel
        );
      } else {
        shippingBenefitRow.hidden = true;
        shippingBenefitRow.setAttribute('hidden', '');
        shippingBenefitEl.innerHTML = '';
      }
    }

    let totalGrossSavings = 0;
    if (showBundleBenefit) totalGrossSavings += pricing.displayCents(bundleSavings);
    if (showPromoBenefit) totalGrossSavings += promoGrossSavingsCents(promoDisplaySavings, drawer);
    if (showGiftBenefit) totalGrossSavings += giftCompareGrossCents(drawer);
    if (shippingIsFree) totalGrossSavings += shippingFlatGrossCents(drawer);

    const hasBenefits =
      showBundleBenefit || showGiftBenefit || showPromoBenefit || shippingIsFree;
    if (benefitsEl) {
      if (hasBenefits) {
        benefitsEl.hidden = false;
        benefitsEl.removeAttribute('hidden');
      } else {
        benefitsEl.hidden = true;
        benefitsEl.setAttribute('hidden', '');
      }
    }

    if (totalSavingsRow && totalSavingsAmount) {
      if (totalGrossSavings > 0) {
        totalSavingsRow.hidden = false;
        totalSavingsRow.removeAttribute('hidden');
        totalSavingsAmount.innerHTML = renderSummarySavingsAmount(totalGrossSavings);
      } else {
        totalSavingsRow.hidden = true;
        totalSavingsRow.setAttribute('hidden', '');
        totalSavingsAmount.innerHTML = '';
      }
    }

    const formatNetAsGross = (netCents) =>
      pricesIncludeVat ? pricing.formatNet(netCents) : moneyNet(netCents);

    // Subtotal = payable items (sale/final), VAT-inclusive — matches checkout merchandise.
    // Shipping SAR settings are already VAT-inclusive — do not gross up again.
    // Total = subtotal + shipping, matching checkout amount due.
    const itemsPayableGross = formatNetAsGross(shopifyItemsNet > 0 ? shopifyItemsNet : netAfterDiscounts);
    if (subtotalEl) subtotalEl.textContent = itemsPayableGross;
    if (summary) summary.dataset.shippingNetCents = String(shippingDisplay);
    if (shippingRow) {
      shippingRow.hidden = shippingIsFree;
      if (shippingIsFree) shippingRow.setAttribute('hidden', '');
      else shippingRow.removeAttribute('hidden');
    }
    if (shippingEl) {
      if (shippingIsFree) {
        shippingEl.innerHTML = '';
      } else {
        shippingEl.textContent = pricing.formatDisplay(shippingDisplay);
      }
    }

    const shopifyTotalGross = shopifyItemsNet > 0 ? pricing.displayCents(shopifyItemsNet) : 0;
    const shippingGross = shippingDisplay > 0 ? shippingDisplay : 0;
    const totalDisplay =
      shopifyTotalGross > 0
        ? shopifyTotalGross + shippingGross
        : pricing.displayCents(netAfterDiscounts) + shippingGross;
    if (totalEl) totalEl.textContent = pricing.formatDisplay(totalDisplay);
  };

  const bind = () => {
    document.addEventListener('ghalia:cart:stack-discount', (e) => {
      const drawer = document.querySelector('[data-cart-drawer]');
      applySummary(e.detail?.cart, e.detail?.state, drawer);
    });
    document.addEventListener('ghalia:cart:pricing', (e) => {
      const drawer = document.querySelector('[data-cart-drawer]');
      applySummary(e.detail?.cart, window.GhaliaCart?.lastStackState, drawer);
    });
    document.addEventListener('ghalia:cart:ui-sync', () => {
      hideLiquidDiscountRows(document.querySelector('[data-cart-drawer]'));
    });
  };

  window.GhaliaCartSummary = { apply: applySummary };

  window.GhaliaPromo = {
    meetsMinimum: qualifyingMeetsPromoMinimum,
    shopifyDiscountNet: (cart, drawer) => {
      const d = drawer || document.querySelector('[data-cart-drawer]');
      return resolvePromoSavingsNet(cart, d);
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
